import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { toAudString } from 'utils/currency';
import { ReactComponent as IconClose } from 'assets/icons/svgs/icon-close.svg';
import { ReactComponent as IconApproved } from 'assets/icons/svgs/icon-approved.svg';
import { DropdownWithMenu } from 'components/UI/DropdownWithMenu/DropdownWithMenu';
import { addDonation } from 'services/PaymentService';
import { appConstants } from 'utils/global-constants';
import { PaymentPageContext } from 'components/Payment/PaymentPageProvider';
import {
  getArrayOfFunds,
  getChosenFundDetails,
  getRoundedDonationAmount,
} from './payment-donation-utils';

import './PaymentDonation.scss';

export const PaymentDonation = (props) => {
  const { Funds, DonationAllowed, DonationOption } = props;

  if (!DonationAllowed || !Funds.length) {
    return null;
  }

  const { order, refreshOrderData } = useContext(PaymentPageContext);

  let defaultFundDetails = Funds[0];
  const [isProcessing, setIsProcessing] = useState(false);
  const [myChoiceInputValue, setMyChoiceInputValue] = useState('');
  const [showDonationPopup, setShowDonationPopup] = useState(true);
  const [roundedDonationAmount, setRoundedDonationAmount] = useState(false);
  const [chosenFundDetails, setChosenFundDetails] = useState(defaultFundDetails);
  const [donationFormState, setDonationFormState] = useState(DonationOption);

  const fundDetails = {
    fundName: chosenFundDetails?.name || defaultFundDetails?.FundName,
    fundImage: chosenFundDetails?.fundImage || defaultFundDetails?.FundImage,
    fundDescription: chosenFundDetails?.fundDescription || defaultFundDetails?.FundDescription,
  };

  const useRoundedDonationAmount = () => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationOptionId: 'donateRound',
      DonationAmount: order.Cart.DonationItems[0].Amount,
    }));
  };

  const useOtherDonationAmount = () => {
    setRoundedDonationAmount(getRoundedDonationAmount(order));
    const donationAmount = defaultFundDetails.DonationAmounts.find(
      (amount) => amount.Key === order.Cart.DonationItems[0].Amount
    );

    setDonationFormState((prevState) => ({
      ...prevState,
      DonationOptionId: donationAmount ? donationAmount.Value : 'other',
      DonationAmount: donationAmount ? donationAmount.Key : order.Cart.DonationItems[0].Amount,
    }));
  };

  const checkForFirstCallFund = () => {
    // Check if there is a donation for the Standalone donation pages (ID 9 is First call Fund)
    if (order.Cart.DonationItems[0].FundId !== '9') {
      defaultFundDetails = Funds.find(
        (fund) => fund.FundTessituraId === order.Cart.DonationItems[0].FundId
      );
    }
  };

  /*
   * When the form first loads OR when the order is updated (i.e. a donation added or removed)
   */
  useEffect(() => {
    const cartHasDonationItems = order?.Cart?.DonationItems?.length > 0;

    /*
     * Determine the state of the form by the DonationItems object (i.e.
     * the Donation form UI should mirror the current donation in the cart)
     */
    if (cartHasDonationItems) {
      checkForFirstCallFund();
      // If the donation amount IS less than 10 then it is assumed to have been a rounded up donation
      const donationIsUnder10Dollars = order.Cart.DonationItems[0].Amount <= 10;

      if (donationIsUnder10Dollars) {
        useRoundedDonationAmount();
      } else {
        useOtherDonationAmount();
      }
    } else {
      // Else just set default values for the Donation form
      setRoundedDonationAmount(getRoundedDonationAmount(order));
      setChosenFundDetails(false);
      setDonationFormState({
        ...DonationOption,
        DonationOptionId: 'donateNo',
      });
    }
  }, [order, DonationOption]);

  const yesDonationSelectedHandler = () => {
    donationFormState.DonationAmount = defaultFundDetails.DonationAmounts[0];
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationAmount: defaultFundDetails.DonationAmounts[0].Key,
      DonationOptionId: 'donateYes',
    }));
  };

  const noDonationSelectedHandler = () => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationAmount: DonationOption.DonationAmount,
      DonationOptionId: 'donateNo',
    }));
    setMyChoiceInputValue('');
  };

  const selectDonationAmountHandler = (donationAmount) => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationAmount: donationAmount,
      DonationOptionId: 'donateYes',
    }));
    setMyChoiceInputValue(donationAmount);
  };

  const removeSelectedDonationHandler = () => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationAmount: DonationOption.DonationAmount,
    }));
  };

  const myChoiceInputFocusHandler = () => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationOptionId: 'other',
    }));
  };

  const myChoiceInputChangeHandler = (event) => {
    // Only allow numeric values
    const donationOptionAmountValue = event.target.value.replace(/\D/g, '');
    setMyChoiceInputValue(donationOptionAmountValue);

    const inputValueIsGreatThanZero = Number.parseFloat(event.target.value) > 0;
    const myChoiceDonationAmount = Number.parseFloat(event.target.value) || 0;

    setDonationFormState((prevState) => ({
      ...prevState,
      DonationAmount: inputValueIsGreatThanZero
        ? myChoiceDonationAmount
        : DonationOption.DonationAmount,
    }));
  };

  const selectRoundedDonationHandler = () => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationAmount: roundedDonationAmount,
      DonationOptionId: 'donateRound',
    }));
    setMyChoiceInputValue('');
  };

  const fundDropdownChangeHandler = (chosenFundId) => {
    setDonationFormState((prevState) => ({
      ...prevState,
      DonationFundId: chosenFundId,
    }));
    const chosenFundFromDropdown = getChosenFundDetails(chosenFundId, Funds);
    setChosenFundDetails(chosenFundFromDropdown);
    setShowDonationPopup(true);
  };

  const fundDialogPopupHandler = () => {
    setShowDonationPopup(false);
  };

  const addDonationHandler = async () => {
    const donationPayload = {
      ...donationFormState,
      FundName: chosenFundDetails.name,
      fundDescription: chosenFundDetails.fundDescription,
      FundImage: chosenFundDetails.fundImage,
    };

    try {
      setIsProcessing(true);
      const response = await addDonation(donationPayload);
      setIsProcessing(false);

      if (response.Status === 1) {
        setMyChoiceInputValue(response.Order.Cart.DonationsTotal);
        refreshOrderData(response.Order);
      } else if (response.ErrorMessage) {
        setDonationFormState((prevState) => ({
          ...prevState,
          DonationError: response.ErrorMessage,
        }));
      } else {
        setDonationFormState((prevState) => ({
          ...prevState,
          DonationError: appConstants.ErrorMessages.DonationCantBeAdded,
        }));
      }
    } catch (e) {
      console.error(e);
      setIsProcessing(false);
      setDonationFormState((prevState) => ({
        ...prevState,
        DonationError: appConstants.ErrorMessages.DonationCantBeAdded,
      }));
    }
  };

  return (
    <div className="cart-donate bg-light-putty">
      <div className="container">
        <h3 className="h4">
          Donate &amp; help disadvantaged young Victorians' experience the arts.
        </h3>
        <ul className="text-bars-container">
          <li>
            <div
              className={`text-bar ${
                donationFormState.DonationOptionId === 'donateYes' ? 'selected' : ''
              }`}
            >
              <div className="row no-gutter">
                <div className="custom-radio">
                  <input
                    id="donateYes"
                    type="radio"
                    name="donateOptions"
                    disabled={order.Cart.DonationItems.length}
                    value="donateYes"
                    checked={
                      donationFormState.DonationOptionId === 'donateYes' ||
                      donationFormState.DonationOptionId === 'other' ||
                      donationFormState.DonationOptionId === 'donateRound' ||
                      myChoiceInputValue
                    }
                    readOnly
                  />
                  <label htmlFor="donateYes" onClick={yesDonationSelectedHandler}>
                    Make a donation
                  </label>
                </div>

                <div className="panel" role="tab">
                  <label className="label-donate">Click to select one of the options below:</label>
                  <div className="form-group inline">
                    {defaultFundDetails.DonationAmounts.map((donationAmount, index) => (
                      <div className="custom-radio-box" key={index}>
                        <input
                          id={donationAmount[index]}
                          disabled={order.Cart.DonationItems.length}
                          type="radio"
                          name="radioAmountOption"
                          value={donationAmount.Value}
                          checked={donationFormState.DonationAmount === donationAmount.Key}
                          readOnly
                        />

                        <label
                          htmlFor={donationAmount[index]}
                          onClick={() => selectDonationAmountHandler(donationAmount.Key)}
                        >
                          {donationAmount.Value}
                        </label>
                      </div>
                    ))}

                    <br className="visible-xs visible-sm" />
                    <div className="custom-radio-box radio-box-other">
                      <div className="custom-radio-box radio-box-other">
                        <input
                          type="text"
                          min="0"
                          className="form-control show-placeholder"
                          placeholder="$ my choice"
                          onChange={myChoiceInputChangeHandler}
                          onFocus={myChoiceInputFocusHandler}
                          disabled={order.Cart.DonationItems.length}
                          onClick={removeSelectedDonationHandler}
                          value={myChoiceInputValue}
                        />
                      </div>
                    </div>
                    <br className="visible-xs visible-sm" />
                    <div className="custom-radio-box radio-box-roundup">
                      <input
                        id="donateAmountRoundup"
                        type="radio"
                        name="radioAmountOption"
                        checked={donationFormState.DonationOptionId === 'donateRound'}
                        onClick={selectRoundedDonationHandler}
                        readOnly
                      />
                      <label htmlFor="donateAmountRoundup">
                        {`Round up my total for '${
                          fundDetails?.fundName
                        }' to the nearest $10 - (${toAudString(roundedDonationAmount)})`}
                      </label>
                    </div>
                  </div>
                  {!order.Cart.DonationItems.length && (
                    <div className="form-group">
                      <div className="input-container">
                        <label htmlFor="selectDonateFunds" className="label-donate label-inline">
                          Whom would you like to donate to?
                        </label>
                        <div className="col-sm-3 donation-dropdown-menu">
                          <DropdownWithMenu
                            menuData={getArrayOfFunds(Funds)}
                            defaultChosenValue={defaultFundDetails.FundName}
                            dropdownChangeHandler={fundDropdownChangeHandler}
                            hideDefaultItem
                            singleColumn
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row no-gutter">
                    <div className="col-md-6" id="donatePopup">
                      {showDonationPopup && (
                        <div
                          className="fund-description"
                          data-fund-id={donationFormState.DonationFundId}
                        >
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            formNoValidate
                          >
                            <IconClose className="icon" onClick={fundDialogPopupHandler} />
                          </button>
                          <img
                            src={fundDetails?.fundImage}
                            alt={fundDetails?.fundName}
                            width="209"
                            height="105"
                            className="fund-image"
                          />
                          <div>
                            {fundDetails?.fundDescription && parse(fundDetails.fundDescription)}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 pull-right">
                      <div className="btn-container">
                        {order.Cart.DonationItems.length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-success pull-right"
                          >
                            <IconApproved className="icon fill-white" />
                            Donation added
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-secondary pull-right"
                            onClick={addDonationHandler}
                            disabled={isProcessing || donationFormState.DonationAmount === 0.0}
                          >
                            Add donation to cart
                          </button>
                        )}
                      </div>

                      <div className="order-total text-right">
                        <p>
                          <small>
                            All donations over $2 are tax deductible. A receipt will be emailed to
                            you.
                          </small>
                        </p>
                        <div className="donation-summary">
                          <span className="selected-fund">
                            Donation amount to <strong>{fundDetails?.fundName}</strong>
                          </span>
                          <br />
                          <span className="selected-amount">
                            <small>AUD </small>
                            <strong>{toAudString(donationFormState.DonationAmount)}</strong>
                          </span>
                        </div>
                        <div className="row-heading">
                          <strong>Order total</strong>
                        </div>
                        <small>AUD</small> <strong>{toAudString(order.Cart.OrderTotal)}</strong>
                        <p>
                          <small className="cart-disclaim">Price is inclusive of GST</small>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {donationFormState.DonationError && (
                  <div className="callout-row">
                    <div className="callout-container callout-error">
                      <p>{donationFormState.DonationError}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </li>

          <li>
            <div
              className={[
                'text-bar',
                donationFormState.DonationOptionId === 'donateNo' ? 'selected' : '',
                order.Cart.DonationItems.length > 0 ? 'disabled' : '',
              ].join(' ')}
            >
              <div className="row no-gutter">
                <div className="col-xs-7 col-sm-6">
                  <div className="custom-radio">
                    <input
                      type="radio"
                      value="donateNo"
                      name="donateOptions"
                      id="donateNo"
                      disabled={order.Cart.DonationItems.length}
                      onClick={noDonationSelectedHandler}
                      checked={donationFormState.DonationOptionId === 'donateNo'}
                      readOnly
                    />
                    <label htmlFor="donateNo">Continue without donation</label>
                  </div>
                </div>

                <div className="col-xs-5 col-sm-6 text-right">
                  <div className="order-total">
                    <div className="row-heading">
                      <strong>Order total</strong>
                    </div>
                    <small>AUD </small>
                    {!order.Cart.DonationItems.length > 0 ? (
                      <strong>{toAudString(order.Cart.OrderTotal)}</strong>
                    ) : null}
                    {order.Cart.DonationItems.length > 0 ? (
                      <strong>
                        {toAudString(order.Cart.OrderTotal - order.Cart.DonationItems[0].Amount)}
                      </strong>
                    ) : null}
                    <p>
                      <small className="cart-disclaim">Price is inclusive of GST</small>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

PaymentDonation.propTypes = {
  DonationAllowed: PropTypes.bool,
  DonationOption: PropTypes.object,
  Funds: PropTypes.array,
};
